import React, { useState, useRef } from 'react';
import { Box, Typography, ClickAwayListener, useMediaQuery, useTheme } from '@mui/material';
import { styled } from '@mui/material/styles';
import DropdownArrowNavIcon from '../icons/DropdownArrowNavIcon';

// Mobile-First Styled Components
const DropdownContainer = styled(Box)(({ theme, width }) => ({
  borderRadius: '10px',
  border: '1px solid #E2E8F0',
  padding: '14px 16px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  cursor: 'pointer',
  backgroundColor: '#ffffff',
  width: width || '100%',
  minHeight: '48px',
  position: 'relative',
  transition: 'all 0.2s ease',
  '&:hover': {
    borderColor: '#0B91D6',
    backgroundColor: '#F8FAFC',
  },
  '&:active': {
    backgroundColor: '#F1F5F9',
  },
  [theme.breakpoints.up('md')]: {
    borderRadius: '8px',
    border: '0.2px solid #A0A0AA',
    padding: '16px',
    minHeight: 'auto',
    width: width || 'auto',
    '&:hover': {
      backgroundColor: 'transparent',
    },
  },
}));

const DropdownText = styled(Typography)(({ theme }) => ({
  fontFamily: 'Inter, sans-serif',
  fontSize: '14px',
  fontWeight: 500,
  color: '#0B91D6',
}));

const ArrowWrapper = styled(Box)(({ theme, isOpen }) => ({
  display: 'flex',
  alignItems: 'center',
  transform: isOpen ? 'rotate(180deg)' : 'rotate(0)',
  transition: 'transform 0.2s ease',
}));

const DropdownMenu = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: 'calc(100% + 4px)',
  left: 0,
  right: 0,
  backgroundColor: '#ffffff',
  border: '1px solid #E2E8F0',
  borderRadius: '12px',
  boxShadow: '0 10px 40px -10px rgba(0, 0, 0, 0.15)',
  zIndex: 2000,
  maxHeight: '280px',
  overflowY: 'auto',
  WebkitOverflowScrolling: 'touch',
  [theme.breakpoints.up('md')]: {
    borderRadius: '8px',
    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
    maxHeight: '200px',
  },
}));

const DropdownItem = styled(Box)(({ theme, selected }) => ({
  padding: '14px 16px',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  backgroundColor: selected ? '#F0F9FF' : 'transparent',
  borderBottom: '1px solid #F1F5F9',
  transition: 'background-color 0.15s ease',
  '&:last-child': {
    borderBottom: 'none',
  },
  '&:hover': {
    backgroundColor: '#F8FAFC',
  },
  '&:active': {
    backgroundColor: '#F1F5F9',
  },
  [theme.breakpoints.up('md')]: {
    padding: '12px 16px',
    borderBottom: 'none',
    '&:hover': {
      backgroundColor: '#F3F4F6',
    },
  },
}));

const SelectedIndicator = styled(Box)(({ theme }) => ({
  width: '8px',
  height: '8px',
  borderRadius: '50%',
  backgroundColor: '#0B91D6',
}));

const ScheduleDropdown = ({ value, options, onChange, width, isMobile }) => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef(null);

  const handleSelect = (option) => {
    onChange(option);
    setIsOpen(false);
  };

  const handleClickAway = () => {
    setIsOpen(false);
  };

  const handleContainerClick = (e) => {
    e.stopPropagation();
    setIsOpen(!isOpen);
  };

  return (
    <ClickAwayListener onClickAway={handleClickAway}>
      <DropdownContainer width={width} onClick={handleContainerClick} ref={containerRef}>
        <DropdownText>{value}</DropdownText>
        <ArrowWrapper isOpen={isOpen}>
          <DropdownArrowNavIcon width={10} height={6} />
        </ArrowWrapper>

        {isOpen && (
          <DropdownMenu>
            {options.map((option) => (
              <DropdownItem
                key={option}
                selected={value === option}
                onClick={(e) => {
                  e.stopPropagation();
                  handleSelect(option);
                }}
              >
                <Typography
                  sx={{
                    fontSize: '14px',
                    fontWeight: value === option ? 600 : 400,
                    color: value === option ? '#0B91D6' : '#121927',
                  }}
                >
                  {option}
                </Typography>
                {value === option && <SelectedIndicator />}
              </DropdownItem>
            ))}
          </DropdownMenu>
        )}
      </DropdownContainer>
    </ClickAwayListener>
  );
};

export default ScheduleDropdown;
